import { Heart } from "./Heart";
import { Noise } from "./Perlin2";
// import Stats from "stats.js";
import lerp from "lerp";
import smoothstep from "smoothstep";

const BASE_HEART_WIDTH = 15;
const BASE_HEART_HEIGHT = 15;
const BASE_GRID_GAP = 4;
const BASE_SCALE = 0.07;

// const BASE_HEART_WIDTH = 30;
// const BASE_HEART_HEIGHT = 30;
// const BASE_GRID_GAP = 4;
// const BASE_SCALE = 0.14;

const DPR = window.devicePixelRatio || 1;
// const DPR = 4;

// Scaled for DPR
const HEART_WIDTH = BASE_HEART_WIDTH * DPR;
const HEART_HEIGHT = BASE_HEART_HEIGHT * DPR;
const GRID_GAP = BASE_GRID_GAP * DPR;
const SCALE = BASE_SCALE * DPR;

type Grid = {
  width: number;
  height: number;
};

const CONFIG = {
  frequency: 10,
  speed: 3,
};

export class PerlinHearts {
  ctx: CanvasRenderingContext2D | null | undefined;
  canvas: HTMLCanvasElement;
  hearts: Record<number, Record<number, Heart>> | undefined;
  grid: Grid | undefined;
  noise: Noise | undefined;
  // stats: Stats;
  raf: number | undefined;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    // this.stats = new Stats();
    // this.stats.showPanel(0);
    // document.body.appendChild(this.stats.dom);
    this.initCanvas();
    this.initHearts();
    this.draw();
  }

  initCanvas() {
    if (!this.canvas) return;

    this.canvas.width = this.canvas.clientWidth * DPR;
    this.canvas.height = this.canvas.clientHeight * DPR;
    this.grid = {
      // width: 10,
      // height: 2,
      width: Math.ceil(this.canvas.width / (HEART_WIDTH + GRID_GAP)),
      height: Math.ceil(this.canvas.height / (HEART_HEIGHT + GRID_GAP)),
    };
    this.noise = new Noise(Math.random());
    this.ctx = this.canvas.getContext("2d");
  }

  initHearts() {
    if (!this.grid) return;
    this.hearts = {};

    const safeRadius = this.canvas.width / 1.25;

    for (let y = 0; y < this.grid.height; y++) {
      for (let x = 0; x < this.grid.width; x++) {
        if (!this.hearts[x]) {
          this.hearts[x] = {};
        }

        const xPos = x * (HEART_WIDTH + GRID_GAP);
        const yPos = y * (HEART_HEIGHT + GRID_GAP);

        // Check if the heart will be inside the ellipsis size
        const isInsideSafeRadius =
          Math.sqrt(
            Math.pow(xPos - this.canvas.width / 2, 2) +
              Math.pow(yPos - this.canvas.height / 2, 2)
          ) < safeRadius;

        const distanceToCircleCenter = isInsideSafeRadius
          ? Math.sqrt(
              Math.pow(xPos - this.canvas.width / 2, 2) +
                Math.pow(yPos - this.canvas.height / 2, 2)
            )
          : 0;

        this.hearts[x][y] = new Heart({
          x: xPos,
          y: yPos,
          width: HEART_WIDTH,
          height: HEART_HEIGHT,
          scale: SCALE,
          rootScale:
            // y < 5
            //   ? lerp(0.25, 1.4, y / 5)
            //   :
            isInsideSafeRadius
              ? lerp(-1, 1, smoothstep(0, safeRadius, distanceToCircleCenter)) *
                1.4
              : 1.5,
        });
      }
    }
  }

  resize() {
    if (!this.ctx || !this.grid) return;

    if (this.raf) {
      cancelAnimationFrame(this.raf);
    }

    if (this.hearts) {
      for (let x = 0; x < this.grid.width; x++) {
        for (let y = 0; y < this.grid.height; y++) {
          delete this.hearts[x][y];
        }
      }
    }

    this.initCanvas();
    this.initHearts();
    this.draw();
  }

  destroy() {
    // this.stats.dom.remove();
    if (this.raf) {
      cancelAnimationFrame(this.raf);
    }
  }

  update(timestamp: number) {
    if (!this.ctx || !this.grid || !this.noise || !this.hearts) return;

    // this.stats.begin();

    for (let x = 0; x < this.grid.width; x++) {
      for (let y = 0; y < this.grid.height; y++) {
        const perlinValue = this.noise.perlin3(
          x / CONFIG.frequency,
          y / CONFIG.frequency,
          timestamp / (5000 / CONFIG.speed)
        );

        this.hearts[x][y].noise = perlinValue;
      }
    }

    this.draw();
  }

  draw() {
    if (!this.ctx || !this.grid || !this.hearts) return;

    this.ctx.fillStyle = "black";
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    if (this.hearts) {
      for (let x = 0; x < this.grid.width; x++) {
        for (let y = 0; y < this.grid.height; y++) {
          this.hearts[x][y].draw(this.ctx);
        }
      }
    }

    // Draw a circle in the center of the grid, with a radius of half the grid width
    // const centerX = this.canvas.width / 2;
    // const centerY = this.canvas.height / 2;
    // const radius = this.canvas.width / 1.25;

    // const circle = new Path2D();
    // circle.ellipse(centerX, centerY, radius, radius, 0, 0, 2 * Math.PI);

    // this.ctx.fillStyle = "#00ff0044";
    // this.ctx.fill(circle);

    // this.stats.end();

    // Request next update frame
    this.raf = requestAnimationFrame(this.update.bind(this));
  }
}
